import React, {
	type ComponentPropsWithoutRef,
	memo,
	useEffect,
	useMemo,
	useContext,
	useState,
} from 'react';
import { fetchQuery, graphql, useRelayEnvironment, type PreloadedQuery } from 'react-relay';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useEpicsFilterData } from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import type { EpicCardType } from '@atlassian/jira-backlog-epics-store/src/types.tsx';
import { useEntryPointToggleTrigger } from '@atlassian/jira-entry-point-toggle-trigger';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import VersionFilter from '@atlassian/jira-filters/src/ui/filters/version/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import type { IssueParent } from '@atlassian/jira-software-backlog-panel/src/services/epic/get/types.tsx';
import { isRelayValidationError } from '@atlassian/jira-software-filters/src/ui/custom-filter-boundary/utils.tsx';
import FilterBar from '@atlassian/jira-software-filters/src/ui/main.tsx';
import type { APIProps as Props } from '@atlassian/jira-software-filters/src/ui/stateless/types.tsx';
import type { VersionFilterRelay } from '@atlassian/jira-software-filters/src/ui/version-filter-relay/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { UNASSIGNED_EPIC_ID } from '../../../../common/constants';
import { DEFAULT_PARENT_ISSUE_TYPE_NAME } from '../../../../model/constants';
import {
	setClearCustomFilterInteractionStartMark,
	setCustomFilterInteractionFeedbackMark,
	setCustomFilterInteractionStartMark,
} from '../../../../services/performance/interaction-metric-custom-filter';
import {
	setInteractionFeedbackMark,
	setInteractionStartMark,
} from '../../../../services/performance/interaction-metric-filter';
import { useGetEpicsAndProject } from '../../../../services/queries/all-epic';
import { VersionPanelEntrypointContext } from '../../../../services/versions-panel-entrypoint';
import { useBacklogDispatch, useBacklogSelector } from '../../../../state';
import { SET_VERSIONS } from '../../../../state/actions/version';
import { getProjects, isCMPScrumBacklog } from '../../../../state/selectors/app';
import { getShowEpicAsPanel } from '../../../../state/selectors/config';
import { useIsCMPBacklog } from '../../../../state/state-hooks/capabilities';
import { BacklogCustomFilter } from './custom-filters';
import messages from './messages';
import { useIssueTypes } from './utils/use-issue-types';

export type PropsWithQueryRefs = Props & {
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null | undefined;
	versionFilterRelayQueryRef: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null | undefined;
};

// eslint-disable-next-line @atlassian/relay/must-use-inline
const ViewBacklogVersionFilterRelayQuery = graphql`
	query viewBacklogVersionFilterRelayQuery($jiraProjectIds: [ID!]!, $first: Int!, $after: String) {
		jira @required(action: THROW) @optIn(to: ["JiraVersionsForProjects"]) {
			versionsForProjects(
				jiraProjectIds: $jiraProjectIds
				first: $first
				after: $after
				filter: [UNRELEASED]
			) @required(action: THROW) @connection(key: "versionBacklogPanelRelay_versionsForProjects") {
				edges @required(action: THROW) {
					...versionFilterRelay_softwareFilters
					node {
						versionId
						name
						project {
							key
						}
					}
				}
			}
		}
	}
`;

// exported for testing
export const BacklogVersionFilterRelay = ({
	versionFilterRelayQueryRef,
	...props
}: Omit<ComponentPropsWithoutRef<typeof VersionFilterRelay>, 'versionFilterRelayFragmentRef'> & {
	versionFilterRelayQueryRef: PreloadedQuery<viewBacklogVersionFilterRelayQuery>;
}) => {
	const { entryPointActions } = useContext(VersionPanelEntrypointContext);

	const environment = useRelayEnvironment();
	const triggerRef = useEntryPointToggleTrigger(entryPointActions);
	const projects = useBacklogSelector(getProjects);
	const tenantContext = useTenantContext();
	const jiraProjectIds = useMemo(
		() =>
			projects.map((project) =>
				JiraProjectAri.create({
					projectId: `${project.id}`,
					siteId: tenantContext.cloudId,
				}).toString(),
			),
		[projects, tenantContext.cloudId],
	);

	const [data, setData] = useState<viewBacklogVersionFilterRelayQuery['response']>();
	useEffect(() => {
		if (jiraProjectIds.length > 0) {
			fetchQuery<viewBacklogVersionFilterRelayQuery>(
				environment,
				ViewBacklogVersionFilterRelayQuery,
				{
					jiraProjectIds,
					first: 500,
					after: null,
				},
				fg('fix_backlog_version_filter_loading_issue')
					? { fetchPolicy: 'store-or-network' }
					: undefined,
			).subscribe({
				next: (response) => {
					setData(response);
				},
			});
		}
	}, [jiraProjectIds, environment]);

	const versions = useMemo(() => {
		if (!data) {
			return [];
		}
		const transformedVersions = data.jira.versionsForProjects.edges
			.filter(Boolean)
			.map(({ node }) => ({
				id: node?.versionId || '',
				name: node?.name || '',
				projectKey: node?.project?.key,
			}));

		return transformedVersions;
	}, [data]);

	const dispatch = useBacklogDispatch();

	useEffect(() => {
		if (data) {
			dispatch({
				type: SET_VERSIONS,
				payload: {
					versions: data.jira.versionsForProjects.edges
						.filter(Boolean)
						.map((edge) => edge.node?.versionId)
						.filter(Boolean),
				},
			});
		}
	}, [dispatch, data]);

	return <VersionFilter versions={versions} isLoading={!data} toggleRef={triggerRef} {...props} />;
};

const isShowParentIssueFilter = (
	isCMPBacklog: boolean,
	isCMPScrum: boolean,
	showEpicPanelToggle: boolean,
	isIssueParents: boolean,
	isParentIssueTypeValid: boolean,
) => {
	let showParentIssueFilter;
	if (isCMPBacklog) {
		if (isCMPScrum) {
			// CMP Scrum backlog
			showParentIssueFilter = true;
		} else {
			// CMP Kanban backlog
			showParentIssueFilter = showEpicPanelToggle || isIssueParents;
		}
	} else {
		// TMP backlog
		showParentIssueFilter = isParentIssueTypeValid;
	}
	return showParentIssueFilter;
};

export const FilterBarWrapper = ({
	customFiltersQueryRef,
	versionFilterRelayQueryRef,
	issueTypes,
	...props
}: PropsWithQueryRefs) => {
	const { formatMessage } = useIntl();

	let parents: EpicCardType[] | IssueParent[] = useMemo(() => [], []);
	let issueParentsTypeName = '';
	let isParentIssueTypeAvailable = false;

	if (fg('jsw_epic_panel_paginated_fetch')) {
		const [
			{
				cards: parentsNew,
				epicIssueTypeName: issueParentsTypeNameNew,
				isEpicIssueTypeAvailable: isParentIssueTypeAvailableNew,
			},
			// eslint-disable-next-line react-hooks/rules-of-hooks
		] = useEpicsFilterData();

		parents = parentsNew;
		issueParentsTypeName = issueParentsTypeNameNew;
		isParentIssueTypeAvailable = isParentIssueTypeAvailableNew;
	} else {
		const {
			parents: parentsOld,
			issueParentsTypeName: issueParentsTypeNameOld,
			isParentIssueTypeAvailable: isParentIssueTypeAvailableOld,
			// eslint-disable-next-line react-hooks/rules-of-hooks
		} = useGetEpicsAndProject(props.boardId, true);

		parents = parentsOld;
		issueParentsTypeName = issueParentsTypeNameOld;
		isParentIssueTypeAvailable = isParentIssueTypeAvailableOld;
	}

	const { issueParentsLabel } = props;

	const issueParents = useMemo(() => {
		const target =
			issueParentsLabel && issueParentsLabel !== DEFAULT_PARENT_ISSUE_TYPE_NAME
				? issueParentsLabel
				: DEFAULT_PARENT_ISSUE_TYPE_NAME.toLowerCase();

		const partialParents = parents.map((parent) => {
			if ('epic' in parent) {
				return {
					id: String(parent.epic.id),
					key: parent.epic.key,
					name: parent.epic.name,
				};
			}
			return {
				id: parent.id,
				key: parent.key,
				name: parent.summary,
			};
		});

		return partialParents && partialParents.length
			? [
					{
						id: String(UNASSIGNED_EPIC_ID),
						name: formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.unassignedIssueTermRefresh
								: messages.unassigned,
							{ target },
						),
					},
					...partialParents,
				]
			: partialParents;
	}, [formatMessage, issueParentsLabel, parents]);

	const isCMPBacklog = useIsCMPBacklog();
	const isCMPScrum = useBacklogSelector(isCMPScrumBacklog);
	const showEpicPanelToggle = useBacklogSelector(getShowEpicAsPanel);
	const hasMultipleProjects = useBacklogSelector((state) => getProjects(state).length > 1);

	const showParentIssueFilter = isShowParentIssueFilter(
		isCMPBacklog,
		isCMPScrum,
		showEpicPanelToggle,
		issueParents.length > 0,
		isParentIssueTypeAvailable,
	);

	const versionFilterProps = {
		VersionFilterRelay: BacklogVersionFilterRelay,
		versionFilterRelayQueryRef,
		isVersionFilterRelayEnabled: true,
	};

	const panelToggleVisibility = {
		showEpicPanelToggle: isCMPBacklog ? showEpicPanelToggle : true,
		showVersionsPanelToggle: true,
	};

	const issueTypesToDisplay = useIssueTypes(issueTypes);

	const handleRelayRequiredFail = (location: string, error: Error) => {
		if (!isRelayValidationError(error)) {
			throw error;
		}
	};

	return (
		<JSErrorBoundary
			id="backlogHeaderControlsBarFilterBar"
			packageName="software-backlog"
			onError={handleRelayRequiredFail}
		>
			<FilterBar
				{...props}
				issueTypes={issueTypesToDisplay}
				issueParents={issueParents}
				issueParentsLabel={issueParentsTypeName}
				showPanelToggle={undefined}
				panelToggleVisibility={panelToggleVisibility}
				showParentIssueMenuIfValuesAreEmpty={showParentIssueFilter}
				showSearchFilterChangeboarding
				hijackBrowserDefaultSearch={!isCMPBacklog}
				screenName="backlog"
				onChangeInteractionFeedback={setInteractionFeedbackMark}
				onChangeInteractionStart={setInteractionStartMark}
				onCustomFilterChangeInteractionFeedback={setCustomFilterInteractionFeedbackMark}
				onCustomFilterChangeInteractionStart={setCustomFilterInteractionStartMark}
				showCustomFilters={isCMPBacklog}
				customFiltersQueryRef={customFiltersQueryRef}
				// @ts-expect-error - TS2345 - Type 'CustomFiltersQueryRef | undefined' is not assignable to type 'PreloadedQuery<viewBacklogCustomFiltersQuery, Record<string, unknown>>'.
				CustomFilter={BacklogCustomFilter}
				placeholder={
					isVisualRefreshEnabled()
						? formatMessage(messages.filterPlaceholder)
						: formatMessage(messages.textFilter)
				}
				isUnassignedAssigneeOptionEnabled
				isUnassignedVersionOptionEnabled={isCMPBacklog}
				onClearCustomFilterInteractionStart={setClearCustomFilterInteractionStartMark}
				hasMultipleProjects={hasMultipleProjects}
				{...versionFilterProps}
			/>
		</JSErrorBoundary>
	);
};

export default memo<PropsWithQueryRefs>(FilterBarWrapper);
